import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PawPrint, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

    const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setError("");
    setLoading(true);
    try {
      const oldUserId = localStorage.getItem("waimbow_user_id");
      const { data } = await api.login(email, password, oldUserId);
      localStorage.setItem("waimbow_session", data.session_token);
      localStorage.setItem("waimbow_user_id", data.user_id);
      window.location.href = "/";
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        setError(err.response?.data?.detail || "Too many attempts. Try again later.");
      } else if (status === 403) {
        setError("Wrong email or password");
      } else {
        setError(err.response?.data?.detail || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-cream flex items-center justify-center px-5"
      data-testid="login-screen"
    >
      <div className="w-full max-w-[400px] bg-white rounded-card shadow-soft p-8 text-center">
        <div className="flex justify-center mb-6">
          <div className="w-12 h-12 rounded-full bg-blush/10 flex items-center justify-center">
            <PawPrint className="text-blush" size={24} />
          </div>
        </div>

        <h1
          className="font-heading text-[32px] text-text-primary tracking-tight mb-2"
          data-testid="login-heading"
        >
          Welcome to Waimbow
        </h1>
        <p className="text-text-secondary text-sm font-sans mb-8">
          Enter your email and password to continue
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
              placeholder="Email"
              className="w-full px-4 py-4 rounded-input border border-border-pink bg-white text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-blush/30 text-sm font-sans"
              data-testid="login-email-input"
              autoFocus
            />
          </div>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}
              placeholder="Password"
              className="w-full px-4 py-4 pr-12 rounded-input border border-border-pink bg-white text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-blush/30 text-sm font-sans"
              data-testid="login-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors p-1"
              data-testid="toggle-password-visibility"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {error && (
            <p
              className="text-blush-dark text-sm text-left"
              data-testid="login-error-message"
            >
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading || !email || !password}
            className="w-full bg-blush hover:bg-blush-dark disabled:opacity-60 text-white py-3.5 rounded-pill text-sm font-medium font-sans transition-colors flex items-center justify-center gap-2"
            data-testid="login-submit-btn"
          >
            {loading ? (
              <>
                {[0, 1, 2].map((i) => (
                  <PawPrint
                    key={i}
                    className="w-4 h-4 text-white animate-paw-walk"
                    style={{ animationDelay: `${i * 0.3}s` }}
                  />
                ))}
              </>
            ) : (
              "Enter"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
